import React, { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useUserPermission } from "@hooks/users";
import moment from "moment-timezone";
import { useFormContext, Controller } from "react-hook-form";

import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import FormSimple from "@components/formGroup/formSimple";
import { Input } from "@components/tailwindComponents";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import InputWithAddOptions from "@components/input/InputWithAddOptions";
import COUNTRY_LIST from "@pages/localStorage/devise";

import { LANGUAGE_LIST, GENDER_LIST } from "../employeForm-constants";
import TakePicture from "./components/takePicture";
import useGenderType from "./hooksGenderType";

function SharedPersonnalInfo({ readOnly = false, isMyProfile = false, isEnterprisePage }) {
  const { control } = useFormContext();
  const { t: countryTranslator } = useTranslation(
    "components.dropdown.dropdownCountry"
  );
  const { t: languageTranslator } = useTranslation("pages.employés.statistics");
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers");
  
  const {
    allGenderTypes,
    addGenderType,
    updateGenderType,
    deleteGenderType
  } = useGenderType();

  const {
    canAccessLegalGender,
    canAccessDateOfBirth,
    canAccessOriginCountry,
    canAccessLanguagesSpoken
  } = useUserPermission();

  const genderOptions = useMemo(() => {
    return allGenderTypes.map(gender => ({
      ...gender,
      title: gender._id,
      showAction: !GENDER_LIST.some(g => g._id === gender._id)
    }));
  }, [allGenderTypes])
  
  const isReadOnly = useCallback((name) => {
    const accessObj = {
      "legalGender": canAccessLegalGender,
      "dateOfBirth": canAccessDateOfBirth,
      "originCountry": canAccessOriginCountry,
      "languagesSpoken": canAccessLanguagesSpoken
    }

    return isEnterprisePage ? readOnly : (readOnly && !Boolean(accessObj[name]));
  }, [ canAccessLegalGender, canAccessDateOfBirth, canAccessOriginCountry, canAccessLanguagesSpoken, readOnly, isEnterprisePage ])

  return (
    <div className="arh-flex arh-items-center arh-bg-white arh-p-4">
      <div className="arh-w-[15.5rem]">
        <Controller
          control={control}
          name="profileImage"
          defaultValue={null}
          render={({ field: { onChange, value } }) => (
            <TakePicture
              onFileChange={onChange}
              selectedImage={value}
              mainTranslator={mainTranslator}
              readOnly={readOnly}
              isMyProfile={isMyProfile}
            />
          )}
        />
      </div>
      <div className="arh-ml-[3.25rem] arh-flex arh-flex-1 arh-flex-wrap arh-items-center">
        <div className="arh-mr-[1.875rem] arh-w-[calc(25%-1.875rem)] arh-min-w-[10.5rem] desktop-l:arh-mr-6">
          <Controller
            control={control}
            name="personnalInformation.legalFirstName"
            defaultValue={""}
            rules={{ required: !readOnly }}
            render={({
              field: { onChange, value },
              fieldState: { invalid }
            }) => (
              <FormSimple
                label={mainTranslator("Prénom légal*")}
                placeholder={mainTranslator("Prénom légal*")}
                ParentForm="arh-mb-[1.75rem]"
                classLabel="arh-flex"
                type="text"
                onChange={onChange}
                value={value}
                invalid={invalid}
                readOnly={readOnly}
              />
            )}
          />
        </div>
        <div className="arh-mr-[1.875rem] arh-w-[calc(25%-1.875rem)] arh-min-w-[10.5rem] desktop-l:arh-mr-6">
          <Controller
            control={control}
            name="personnalInformation.legalName"
            defaultValue={""}
            rules={{ required: !readOnly }}
            render={({
              field: { onChange, value },
              fieldState: { invalid }
            }) => (
              <FormSimple
                label={mainTranslator("Nom légal*")}
                placeholder={mainTranslator("Nom légal*")}
                ParentForm="arh-mb-[1.75rem]"
                classLabel="arh-flex"
                type="text"
                onChange={onChange}
                value={value}
                invalid={invalid}
                readOnly={readOnly}
              />
            )}
          />
        </div>
        <div className="arh-mr-[1.875rem] arh-w-[calc(25%-1.875rem)] arh-min-w-[10.5rem] desktop-l:arh-mr-6">
          <Controller
            control={control}
            name="personnalInformation.legalGender"
            defaultValue={""}
            rules={{ required: !isReadOnly("legalGender") }}
            render={({
              field: { onChange, value },
              fieldState: { invalid }
            }) => (
              <div className="arh-mb-[1.75rem]">
                <label
                  className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium 
                  ${isReadOnly("legalGender") ? "arh-text-greyDisabledText" : invalid ? "arh-text-[#F60A20]" : "arh-text-black"}
                    `}
                >
                  {mainTranslator("Genre légal*")}
                </label>
                <InputWithAddOptions
                  defaultText={mainTranslator("Genre légal*")}
                  optionsList={genderOptions}
                  secondDefaultText={mainTranslator("Ajouter")}
                  name="gender"
                  value={value?.name}
                  optionListLabel="name"
                  onChange={(event) => {
                    if (event?.title) {
                      onChange(event.title);
                    }
                    else {
                      if (event?.target?.dataset?.value) {
                        const selectedGender = genderOptions.find(g => g.name === event?.target?.dataset?.value);
                        if (selectedGender) {
                          onChange(selectedGender.title);
                        }
                      }
                    }
                  }}
                  addItem={addGenderType}
                  editItem={updateGenderType}
                  deleteItem={deleteGenderType}
                  validateDropdown={invalid}
                  showAction={isEnterprisePage}
                  hideAdd={!isEnterprisePage}
                  disabled={isReadOnly("legalGender")}
                />
              </div>
            )}
          />
        </div>
        <div className="arh-mr-[1.875rem] arh-w-[calc(25%-1.875rem)] arh-min-w-[10.5rem] desktop-l:arh-mr-6">
          <Controller
            control={control}
            name="personnalInformation.dateOfBirth"
            defaultValue={""}
            render={({ field: { onChange, value } }) => (
              <div className="flex-column calendar_with_btn employerThemeDatePicker wadeDatePicker arh-mb-[1.75rem] arh-flex arh-w-full">
                <label
                  className={`arh-mb-1 arh-w-full arh-font-poppins arh-text-xs arh-font-medium invalid:arh-text-invalidColorTextInput ${
                    isReadOnly("dateOfBirth")
                      ? "arh-text-greyDisabledText"
                      : "arh-text-darkColor"
                  }`}
                >
                  {mainTranslator("Date de naissance")}
                </label>
                {isReadOnly("dateOfBirth") ? (
                  <Input
                    name="selectedDate"
                    value={value ? moment(value).format("LL") : ""}
                    placeholder={mainTranslator("Date de naissance")}
                    disabled
                  />
                ) : (
                  <DefaultDatePicker
                    className="focus_green dateOfBirth"
                    name="selectedDate"
                    placeholder="Date"
                    dateValue={value}
                    setDateValue={onChange}
                    classPosition="calendar_right"
                  />
                )}
              </div>
            )}
          />
        </div>
        <div className="arh-mr-[1.875rem] arh-w-[calc(25%-1.875rem)] arh-min-w-[10.5rem] desktop-l:arh-mr-6">
          <Controller
            control={control}
            name="personnalInformation.firstNameUsed"
            defaultValue={""}
            render={({ field: { onChange, value } }) => (
              <FormSimple
                label={mainTranslator("Prénom utilisé")}
                placeholder={mainTranslator("Prénom utilisé")}
                ParentForm="arh-mb-[1.75rem]"
                classLabel="arh-flex"
                type="text"
                onChange={onChange}
                value={value}
                readOnly={readOnly}
              />
            )}
          />
        </div>
        <div className="arh-mr-[1.875rem] arh-w-[calc(25%-1.875rem)] arh-min-w-[10.5rem] desktop-l:arh-mr-6">
          <Controller
            control={control}
            name="personnalInformation.nameUsed"
            defaultValue={""}
            render={({ field: { onChange, value } }) => (
              <FormSimple
                label={mainTranslator("Nom utilisé")}
                placeholder={mainTranslator("Nom utilisé")}
                ParentForm="arh-mb-[1.75rem]"
                classLabel="arh-flex"
                type="text"
                onChange={onChange}
                value={value}
                readOnly={readOnly}
              />
            )}
          />
        </div>
        <div className="arh-mr-[1.875rem] arh-w-[calc(25%-1.875rem)] arh-min-w-[10.5rem] desktop-l:arh-mr-6">
          <Controller
            control={control}
            name="personnalInformation.originCountry"
            defaultValue={""}
            render={({ field: { onChange, value } }) => (
              <ListboxSimple
                labelActive={true}
                classLabel="arh-flex"
                label={mainTranslator("Pays d'origine")}
                classParentList="arh-mb-[1.75rem]"
                placeholder={mainTranslator("Pays d'origine")}
                name="country"
                optionList={COUNTRY_LIST}
                optionValueAttibut="pays"
                optionTextAttribut="pays"
                selectedValue={value}
                setSelectedValue={onChange}
                customTranslator={countryTranslator}
                withSearch
                readOnly={isReadOnly("originCountry")}
              />
            )}
          />
        </div>
        <div className="arh-mr-[1.875rem] arh-w-[calc(25%-1.875rem)] arh-min-w-[10.5rem]">
          <Controller
            control={control}
            name="personnalInformation.spokenLanguages"
            defaultValue={""}
            render={({ field: { onChange, value } }) => (
              <ListboxSimple
                labelActive={true}
                classLabel="arh-flex"
                label={mainTranslator("Langues parlées")}
                classParentList="arh-mb-[1.75rem]"
                placeholder={mainTranslator("Langues parlées")}
                name="language"
                optionList={LANGUAGE_LIST}
                selectedValue={value}
                setSelectedValue={onChange}
                customTranslator={languageTranslator}
                withSearch
                multiple
                readOnly={isReadOnly("languagesSpoken")}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default SharedPersonnalInfo;
